import { useCallback, useEffect, useRef } from 'react';

/**
 * The add / update / rename / delete mechanics shared by every lambda list editor.
 *
 * Four editors carried their own copy — the phx LambdaListEditor, CondorLambdaListEditor,
 * ValkyrieLambdaListEditor and ValkyrieQueueConsumerListEditor. This is deliberately HEADLESS: the
 * per-template JSX and the per-template item editor stay where they are, because that split is the
 * documented way to add a template. Unifying the components instead would make the shared shell
 * accumulate the union of every template's props (Valkyrie already needs `queueNames`).
 *
 * Config lambdas are keyed by NAME in an object, so rename is a delete + re-insert and a collision
 * has to be refused — that is the part worth having in one place.
 */
export interface NamedItemRef {
  focus: () => void;
}

export function useNamedItemMap<T>(
  items: Record<string, T>,
  onChange: (next: Record<string, T>) => void,
  newItem: () => T,
  opts: {
    /** Prefix for a generated name; the suffix is chosen so the key is unique. */
    namePrefix?: string;
    /** Fired after a successful rename — the queue-consumer editor re-points queue references. */
    onRenamed?: (oldName: string, newName: string) => void;
  } = {},
) {
  const { namePrefix = 'new_lambda', onRenamed } = opts;
  const newlyAddedRef = useRef<string | null>(null);
  const refs = useRef<Record<string, NamedItemRef | null>>({});
  const containers = useRef<Record<string, HTMLElement | null>>({});

  const add = useCallback(() => {
    // Suffix with a counter, not a timestamp: two adds inside the same millisecond used to collide
    // and silently overwrite the first one.
    let name = `${namePrefix}_1`;
    for (let i = 1; items[name] !== undefined; i += 1) name = `${namePrefix}_${i + 1}`;
    onChange({ ...items, [name]: newItem() });
    newlyAddedRef.current = name;
  }, [items, onChange, newItem, namePrefix]);

  // Focus the name input of a just-added row once the DOM has it, and scroll it into view when the
  // caller also registered a container element (a long lambda list appends off-screen otherwise).
  useEffect(() => {
    const name = newlyAddedRef.current;
    if (name && refs.current[name]) {
      requestAnimationFrame(() => {
        containers.current[name]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        refs.current[name]?.focus();
        newlyAddedRef.current = null;
      });
    }
  }, [items]);

  const update = useCallback(
    (name: string, field: keyof T, value: unknown) => {
      onChange({ ...items, [name]: { ...items[name], [field]: value } });
    },
    [items, onChange],
  );

  const remove = useCallback(
    (name: string) => {
      const next = { ...items };
      delete next[name];
      onChange(next);
    },
    [items, onChange],
  );

  const rename = useCallback(
    (oldName: string, newName: string) => {
      // Refuse a no-op, a blank name, and a collision — the last would drop the existing entry.
      if (oldName === newName || !newName.trim() || items[newName] !== undefined) return;
      const next: Record<string, T> = {};
      // Preserve insertion order so a rename does not jump the row to the bottom of the list.
      for (const [k, v] of Object.entries(items)) next[k === oldName ? newName : k] = v;
      onChange(next);
      onRenamed?.(oldName, newName);
    },
    [items, onChange, onRenamed],
  );

  const registerRef = useCallback(
    (name: string) => (r: NamedItemRef | null) => {
      refs.current[name] = r;
    },
    [],
  );

  /** Register the row's outer element to also scroll it into view on add. */
  const registerContainer = useCallback(
    (name: string) => (el: HTMLElement | null) => {
      containers.current[name] = el;
    },
    [],
  );

  return { add, update, remove, rename, registerRef, registerContainer };
}
